import { z } from "zod";

/** Exact question and host-owned customer wording share this persisted boundary. */
export const FollowUpPromiseTextSchema = z.string().trim().min(1).max(2_000);
const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/);
const date = z.iso.date();
const instant = z.iso.datetime({ offset: true });

/** The three choices the rep can make on a factual yes/no question. */
export const QuestionDecisionInputSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("yes"),
    scope: z.enum(["thread", "campaign"]).default("thread"),
  }),
  z.strictObject({
    kind: z.literal("no"),
    scope: z.enum(["thread", "campaign"]).default("thread"),
  }),
  z.strictObject({
    kind: z.literal("need_to_check"),
    checker: z.string().trim().min(1).max(200),
    followUpOn: date,
  }),
]);
export type QuestionDecisionInput = z.infer<typeof QuestionDecisionInputSchema>;

/**
 * The promise the host, rather than the browser or model, authorizes.
 *
 * `commitmentPhrase` is the only customer-facing promise this record licenses. Keeping the exact
 * sentence beside its authority lets a deterministic rule reject a different date, checker or
 * wording even when a model calls the altered sentence supported.
 */
const FollowUpPromiseBaseSchema = z.strictObject({
  id,
  questionKey: id,
  speechActId: FollowUpPromiseTextSchema,
  question: FollowUpPromiseTextSchema,
  topic: z.string().trim().min(1).max(48),
  checker: z.string().trim().min(1).max(200),
  followUpOn: date,
  authorizedBy: id,
  authorizedAt: instant,
  commitmentPhrase: FollowUpPromiseTextSchema,
  status: z.enum(["open", "answered"]).default("open"),
});

export const AuthorizedFollowUpPromiseSchema = FollowUpPromiseBaseSchema.superRefine(
  (promise, context) => {
    if (promise.followUpOn < promise.authorizedAt.slice(0, 10))
      context.addIssue({
        code: "custom",
        path: ["followUpOn"],
        message: "A follow-up date cannot already have passed when the rep authorizes it",
      });
    if (promise.commitmentPhrase !== commitmentPhrase(promise))
      context.addIssue({
        code: "custom",
        path: ["commitmentPhrase"],
        message: "The follow-up wording must be generated from the stored promise",
      });
  },
);
export type AuthorizedFollowUpPromise = z.infer<typeof AuthorizedFollowUpPromiseSchema>;

/** Minimal current-act projection that model stages may read. No authority or control-plane IDs. */
export const ModelFollowUpPromiseSchema = z.strictObject({
  speech_act_id: FollowUpPromiseTextSchema,
  question: FollowUpPromiseTextSchema,
  checker: z.string().trim().min(1).max(200),
  follow_up_on: date,
  commitment_phrase: FollowUpPromiseTextSchema,
});
export type ModelFollowUpPromise = z.infer<typeof ModelFollowUpPromiseSchema>;

/** What crosses the answer boundary after the host has bound it to the current question. */
export const RepQuestionResponseSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.enum(["yes", "no"]),
    questionKey: id,
    speechActId: FollowUpPromiseTextSchema,
    question: FollowUpPromiseTextSchema,
    scope: z.enum(["thread", "campaign"]),
  }),
  z.strictObject({
    kind: z.literal("need_to_check"),
    promise: AuthorizedFollowUpPromiseSchema,
  }),
]);
export type RepQuestionResponse = z.infer<typeof RepQuestionResponseSchema>;

/** Rep-facing lifecycle. Answered is not kept until its follow-up draft is reported sent. */
export const PlanFollowUpPromiseSchema = FollowUpPromiseBaseSchema.omit({ status: true }).extend({
  state: z.enum(["open", "answer_ready", "kept"]),
  answeredAt: instant.nullable(),
  keptAt: instant.nullable(),
});
export type PlanFollowUpPromise = z.infer<typeof PlanFollowUpPromiseSchema>;

const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "be",
  "can",
  "call",
  "could",
  "does",
  "for",
  "include",
  "is",
  "it",
  "our",
  "planned",
  "the",
  "this",
  "we",
  "will",
  "with",
  "would",
  "you",
]);

/** A short deterministic debt label; the full exact question stays on the promise. */
export function promiseTopic(question: string): string {
  const words = question.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  return words.find((word) => !STOP_WORDS.has(word))?.slice(0, 48) ?? "question";
}

function readableDate(value: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00.000Z`));
}

export function commitmentPhrase(input: {
  checker: string;
  question: string;
  followUpOn: string;
}): string {
  return `I’ll check with ${input.checker.trim()} about “${input.question.trim()}” and get back to you by ${readableDate(input.followUpOn)}.`;
}

export function authorizeFollowUpPromise(input: {
  id: string;
  questionKey: string;
  speechActId: string;
  question: string;
  checker: string;
  followUpOn: string;
  authorizedBy: string;
  authorizedAt: string;
}): AuthorizedFollowUpPromise {
  return AuthorizedFollowUpPromiseSchema.parse({
    ...input,
    topic: promiseTopic(input.question),
    commitmentPhrase: commitmentPhrase(input),
    status: "open",
  });
}

export function responseMatchesQuestion(
  response: RepQuestionResponse,
  question: { key: string; question: string; speech_act_id?: string | null | undefined },
): boolean {
  const speechActId = question.speech_act_id ?? null;
  return response.kind === "need_to_check"
    ? response.promise.questionKey === question.key &&
        response.promise.question === question.question &&
        response.promise.speechActId === speechActId
    : response.questionKey === question.key &&
        response.question === question.question &&
        response.speechActId === speechActId;
}
